import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, type VideoItem } from '@/mocks/videos';
import { articles, type ArticleItem } from '@/mocks/articles';
import { videoCategories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

function VideoCardSmall({ video }: { video: VideoItem }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group flex items-center gap-3 p-2 rounded-lg hover:bg-background-200/50 transition-colors cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg w-28 md:w-36 aspect-video flex-shrink-0">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-foreground-200 whitespace-nowrap">
          {video.duration}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
          {video.title}
        </h4>
        <div className="flex items-center gap-2 mt-1 text-[10px] text-foreground-500 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
            {video.rating}
          </span>
          <span>{video.views.toLocaleString()}次</span>
        </div>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const isVideo = id && id.startsWith('v');
  const item = useMemo(() => {
    if (!id) return null;
    if (id.startsWith('v')) return videos.find(v => v.id === id) || null;
    return articles.find(a => a.id === id) || null;
  }, [id]);

  useEffect(() => {
    if (id && !item) {
      navigate('/not-found', { replace: true });
    }
  }, [id, item, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  useEffect(() => {
    if (item && 'views' in item) {
      setLikeCount(Math.floor((item as VideoItem | ArticleItem).views / 10));
    }
  }, [item]);

  if (!item) return null;

  const isVideoItem = isVideo && 'duration' in item;
  const videoItem = isVideoItem ? (item as VideoItem) : null;
  const articleItem = !isVideoItem ? (item as ArticleItem) : null;

  const catNameMap: Record<string, string> = {
    dianying: '电影',
    dianshiju: '电视剧',
    zongyi: '综艺',
    dongman: '动漫',
    jilupian: '纪录片',
    shaoer: '少儿',
  };

  const categoryLabel = catNameMap[item.category] || item.category;

  const relatedVideos = videos
    .filter(v => v.category === item.category && v.id !== item.id)
    .slice(0, 6);

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      setLikeCount(prev => prev + 1);
    } else {
      setLiked(false);
      setLikeCount(prev => prev - 1);
    }
  };

  const pageSchema = isVideoItem && videoItem ? {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: videoItem.title,
    description: videoItem.description,
    thumbnailUrl: videoItem.thumbnail,
    duration: videoItem.duration,
    uploadDate: videoItem.date,
    contentUrl: `https://yaojingvideo.com/article/${videoItem.id}`,
    embedUrl: `https://yaojingvideo.com/article/${videoItem.id}`,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: videoItem.views,
    },
  } : articleItem ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleItem.title,
    description: articleItem.excerpt,
    image: articleItem.image,
    author: {
      '@type': 'Organization',
      name: articleItem.author,
    },
    datePublished: articleItem.date,
    publisher: {
      '@type': 'Organization',
      name: '妖精视频',
    },
  } : undefined;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yaojingvideo.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://yaojingvideo.com/category/${item.category}` },
      { '@type': 'ListItem', position: 3, name: 'title' in item && 'title' in item ? (item as { title: string }).title : '' },
    ],
  };

  return (
    <>
      <SEO
        title={`${(item as { title: string }).title} - 妖精视频`}
        description={'description' in item ? (item as VideoItem).description : (item as ArticleItem).excerpt}
        keywords={`${(item as { title: string }).title},${categoryLabel},妖精视频,在线观看`}
        canonicalUrl={`https://yaojingvideo.com/article/${id}`}
        ogType={isVideoItem ? 'video.movie' : 'article'}
        ogImage={'thumbnail' in item ? (item as VideoItem).thumbnail : (item as ArticleItem).image}
        article={!isVideoItem ? {
          publishedTime: 'date' in item ? (item as ArticleItem).date : undefined,
          author: 'author' in item ? (item as ArticleItem).author : undefined,
          tags: 'tags' in item ? (item as ArticleItem).tags : undefined,
        } : undefined}
        schema={{ ...pageSchema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main>
        <div className="bg-background-100">
          <div className="container-site py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel, href: `/category/${item.category}` },
                { label: (item as { title: string }).title },
              ]}
            />
          </div>
        </div>

        <div className="container-site py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <article className="lg:col-span-2">
              {isVideoItem && videoItem ? (
                <>
                  <div className="relative overflow-hidden rounded-xl aspect-video mb-6 bg-background-200">
                    <img
                      src={videoItem.thumbnail}
                      alt={videoItem.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-primary-500 rounded-full shadow-2xl cursor-pointer hover:bg-primary-600 transition-colors">
                        <i className="ri-play-fill text-background-50 dark:text-foreground-950 text-3xl md:text-4xl"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 rounded-full text-sm text-foreground-200">
                      {videoItem.duration}
                    </div>
                    <div className="absolute top-3 left-3 px-3 py-1 bg-primary-500 rounded-full text-sm text-background-50 dark:text-foreground-950 font-medium">
                      {videoItem.quality}
                    </div>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                    {videoItem.title}
                  </h1>

                  <div className="flex items-center gap-4 mb-6 flex-wrap">
                    <span className="flex items-center gap-1 text-sm text-foreground-500">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong>{videoItem.rating}</strong> 分
                    </span>
                    <span className="text-sm text-foreground-500">
                      <strong>{videoItem.views.toLocaleString()}</strong> 次播放
                    </span>
                    <span className="text-sm text-foreground-500">{videoItem.year}</span>
                    <span className="text-sm text-foreground-500">来源：<strong>{videoItem.source}</strong></span>
                  </div>

                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {videoItem.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                    {videoItem.description}
                  </p>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleLike}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        liked
                          ? 'bg-primary-500/10 text-primary-500 border border-primary-500/30'
                          : 'bg-background-200 text-foreground-600 hover:text-foreground-900 border border-transparent'
                      }`}
                    >
                      <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
                      {likeCount.toLocaleString()}
                    </button>
                    <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-600 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </>
              ) : articleItem ? (
                <>
                  <div className="relative overflow-hidden rounded-xl aspect-[21/9] mb-6 bg-background-200">
                    <img
                      src={articleItem.image}
                      alt={articleItem.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                    {articleItem.title}
                  </h1>

                  <div className="flex items-center gap-4 mb-6 flex-wrap text-sm text-foreground-500">
                    <span>{articleItem.author}</span>
                    <span>{articleItem.date}</span>
                    <span>
                      <strong>{articleItem.views.toLocaleString()}</strong> 次阅读
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {articleItem.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div
                    className="prose prose-invert max-w-none text-foreground-600 leading-relaxed text-sm md:text-base
                      [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                      [&_p]:mb-4 [&_p]:leading-relaxed
                      [&_strong]:text-foreground-800"
                    dangerouslySetInnerHTML={{ __html: articleItem.content }}
                  />

                  <div className="flex items-center gap-3 mt-8 pt-6 border-t border-background-300/30">
                    <button
                      onClick={handleLike}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        liked
                          ? 'bg-primary-500/10 text-primary-500 border border-primary-500/30'
                          : 'bg-background-200 text-foreground-600 hover:text-foreground-900 border border-transparent'
                      }`}
                    >
                      <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
                      {likeCount.toLocaleString()}
                    </button>
                    <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-600 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>

                  <nav className="flex items-center justify-between mt-8 pt-6 border-t border-background-300/30">
                    <Link
                      to={`/article/${articles[(articles.findIndex(a => a.id === articleItem.id) - 1 + articles.length) % articles.length].id}`}
                      className="flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line"></i>
                      上一篇
                    </Link>
                    <Link
                      to={`/article/${articles[(articles.findIndex(a => a.id === articleItem.id) + 1) % articles.length].id}`}
                      className="flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      下一篇
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </nav>
                </>
              ) : null}
            </article>

            <aside className="lg:col-span-1">
              <div className="sticky top-20">
                <div className="bg-background-100 rounded-xl p-5 border border-background-300/30 mb-6">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-play-list-2-line text-primary-500"></i>
                    相关推荐
                  </h3>
                  <div className="space-y-2">
                    {relatedVideos.map(video => (
                      <VideoCardSmall key={video.id} video={video} />
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-primary-500/10 to-accent-500/10 rounded-xl p-5 border border-primary-500/10">
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    妖精视频
                  </h3>
                  <p className="text-xs text-foreground-500 mb-4 leading-relaxed">
                    免费在线视频平台，聚合西瓜视频、91视频等高清资源，电视剧全集、最新电影免费观看。
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    浏览更多内容
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}